// Standalone host for editing business config — separate from
// EditMoreDetailsModal/MoreTab entirely now that config lives on its own
// dedicated /config endpoints rather than inside moreDetails. Fetches via
// the shared BusinessConfigDb cache (useBusinessConfig), snapshots a draft
// on open, and PUTs only the fields the owner actually touched.
import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../../dataManagement/uiDb/UiDb";
import { useBusinessConfig } from "../../../../hooks/useBusinessConfig";
import { updateBusinessConfig } from "../../../../services/thunks/BusinessConfigThunks";
import { BusinessConfigData } from "../../../../interfaces/BusinessObject";
import { useNotification } from "../../../shared/contexts/NotificationContext";
import { NotificationType } from "../../../../constants/Enums";
import useScrollLock from "../../../../hooks/useScrollLock";
import BusinessConfigSectionEditor from "../BusinessTabs/tabs/more/sections/BusinessConfigSectionEditor";
// Reuses EditMoreDetailsModal's overlay/header/footer shell classes --
// plain global CSS, not a module, so sharing it here is intentional rather
// than duplicating ~150 lines of modal chrome for a second modal.
import "../BusinessTabs/tabs/more/edit/styles/EditMoreDetailsModal.css";

interface BusinessConfigEditorModalProps {
  isOpen: boolean;
  businessId: string;
  onClose: () => void;
}

const BusinessConfigEditorModal: React.FC<BusinessConfigEditorModalProps> = ({
  isOpen,
  businessId,
  onClose,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const dispatch: AppDispatch = useDispatch();
  const { triggerNotification } = useNotification();
  const { config } = useBusinessConfig(businessId);

  const [draft, setDraft] = useState<BusinessConfigData>({});
  // Snapshot taken at open time — diffed against `draft` on save so only
  // the fields the owner actually touched this session are PUT. A field
  // untouched this session keeps the SAME object reference as the
  // snapshot (BusinessConfigSectionEditor's onChange only replaces the key
  // it's changing, via `{...safe, key: next}`), so reference inequality is
  // enough to detect a real change.
  const snapshotRef = useRef<BusinessConfigData>({});
  const [isSaving, setIsSaving] = useState(false);

  useScrollLock(isOpen, overlayRef, ".business-config-modal__body");

  useEffect(() => {
    if (isOpen) {
      const snapshot = config ?? {};
      snapshotRef.current = snapshot;
      setDraft(snapshot);
    }
  }, [isOpen, config]);

  const handleSave = async () => {
    const snapshot = snapshotRef.current;
    const fields: Record<string, unknown> = {};
    (Object.keys(draft) as Array<keyof BusinessConfigData>).forEach((key) => {
      if (draft[key] !== snapshot[key]) fields[key] = draft[key];
    });

    if (Object.keys(fields).length === 0) {
      onClose();
      return;
    }

    setIsSaving(true);
    try {
      await dispatch(
        updateBusinessConfig({ businessId, fields: fields as Partial<BusinessConfigData> })
      ).unwrap();
      triggerNotification("Business config updated!", NotificationType.SUCCESS);
      onClose();
    } catch {
      triggerNotification("Couldn't save business config. Please try again.", NotificationType.ERROR);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="edit-more-modal__overlay" ref={overlayRef} onClick={onClose}>
      <div className="edit-more-modal" onClick={(e) => e.stopPropagation()}>
        <header className="edit-more-modal__header">
          <button className="edit-more-modal__close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
          <h2 className="edit-more-modal__title">Business config</h2>
          <div style={{ width: "2.125rem" }} />
        </header>

        <div className="edit-more-modal__body business-config-modal__body">
          <BusinessConfigSectionEditor
            value={draft}
            onChange={(next) => setDraft(next as BusinessConfigData)}
          />
        </div>

        <footer className="edit-more-modal__footer">
          <button
            type="button"
            className="edit-more-modal__cancel"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="edit-more-modal__save"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default BusinessConfigEditorModal;
